'use client';

import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Minus,
  Plus,
  RefreshCw,
  Waves,
  X,
} from 'lucide-react';
import SpinePlayer from './spine-player';
import LostSwordHeroContent from './lostsword-hero-content';
import PlatformEngagement from './platform-engagement';
import { staticUrl } from '../lib/config/static-assets';
import { Input } from '@/components/ui/input';
import {
  NativeSelect,
  NativeSelectOption,
} from '@/components/ui/native-select';
import { heroDirectory, games, heroUrl } from '../lib/heroes/catalog';
import { heroArchiveUrl } from '../lib/heroes/routes';
import type { HeroDirectoryEntry } from '../lib/heroes/directory-types';
import { statsAtLevel, skillRankAtLevel } from '../lib/heroes/calculations';
import type { HeroData } from '../lib/heroes/types';
import { coverTheme } from '../lib/heroes/theme';
import { coverPaletteFromImage, type CoverPalette } from '../lib/heroes/cover-palette';
import { orderedActions } from '../lib/heroes/actions';
import '../app/v2/v2.css';
import './hero-template.css';

function guardianSdActionLabel(actionId: string) {
  const id = actionId.replace(/^\[([^\]]+)\]/, '$1_').toLowerCase();
  const actionNames: Record<string, string> = {
    run: '跑步',
    walk: '行走',
    attack: '攻击',
    damaged: '受伤',
    idle: '待机',
    wait: '待机',
    stand: '待机',
    empty: '空闲',
    mad: '生气',
    smile: '微笑',
    tired: '疲惫',
    surprise: '惊讶',
    doyagao: '得意',
  };
  const directionNames: Record<string, string> = {
    front: '正面',
    font: '正面',
    back: '背面',
    left: '左侧',
    right: '右侧',
  };
  const parts = id.split('_');
  const action = parts.find((part) => actionNames[part]);
  const direction = parts.find((part) => directionNames[part]);
  const group = id.startsWith('down_') ? '低姿态' : id.startsWith('emo_') ? '表情' : '';
  const label = action ? actionNames[action] : actionId;
  const suffix = direction ? ` · ${directionNames[direction]}` : '';
  return group ? `${group} · ${label}${suffix}` : `${label}${suffix}`;
}

function orderGuardianSdActions(actions: string[]) {
  return [...actions].sort((left, right) => {
    const priority = (action: string) => {
      const id = action.replace(/^\[[^\]]+\]/, '').toLowerCase();
      if (/^(?:idle|wait|stand)_(?:front|font)$/.test(id)) return 0;
      if (/(?:idle|wait|stand).*?(?:front|font)/.test(id)) return 1;
      return 2;
    };
    return priority(left) - priority(right) || left.localeCompare(right);
  });
}
export default function HeroDetail({
  hero,
  directory = heroDirectory,
  startAtContent = false,
  initialContentSection = 'skills',
  initialSectionOffset,
  onOpenHeroDirectory,
  onNavigateHero,
}: {
  hero: HeroData;
  directory?: HeroDirectoryEntry[];
  startAtContent?: boolean;
  initialContentSection?: 'skills' | 'archive';
  initialSectionOffset?: number;
  onOpenHeroDirectory?: () => void;
  onNavigateHero?: (href: string) => void;
}) {
  const { skills, quotes: heroQuotes, passives: passiveLevels } = hero;
  const game = games[hero.gameId as keyof typeof games];
  const guardianPassiveGroups =
    hero.gameId === 'guardian-tales'
      ? [
          {
            id: 'weapon-breakthrough',
            title: '专属武器突破',
            items: passiveLevels.filter(
              (item) => item.category === 'weaponBreakthrough',
            ),
          },
          {
            id: 'blessing-breakthrough',
            title: '祝福突破',
            items: passiveLevels.filter((item) => item.category === 'blessing'),
          },
        ].filter((group) => group.items.length > 0)
      : [];
  const heroes = directory.filter((item) => item.gameId === hero.gameId);
  // Skill rank controls intentionally share the title row on all viewports.
  const [heroQuote, setHeroQuote] = useState(
    heroQuotes[heroQuotes.length - 1] ?? '',
  );
  const coverIntro =
    hero.gameId === 'guardian-tales' && hero.story
      ? (hero.story.trim().match(/^.*?[。！？!?]/)?.[0] ?? hero.story.trim())
      : heroQuote;
  const [activeForm, setActiveForm] = useState(hero.forms[0].id);
  const [mobileSelector, setMobileSelector] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 760px)');
    const update = () => setMobileSelector(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const [activeSdForm, setActiveSdForm] = useState(hero.forms[0].id);
  const [activeSdAction, setActiveSdAction] = useState('wait');
  const [discoveredSdActions, setDiscoveredSdActions] = useState<string[]>([]);
  const activePortrait =
    hero.forms.find((form) => form.id === activeForm) ?? hero.forms[0];
  const [coverPalette, setCoverPalette] = useState<CoverPalette | null>(null);
  const [coverPaletteReady, setCoverPaletteReady] = useState(false);
  useEffect(() => {
    let current = true;
    const source = activePortrait.fallback || hero.card || activePortrait.head || hero.head;
    void coverPaletteFromImage(source).then((palette) => {
      if (current) {
        setCoverPalette(palette);
        setCoverPaletteReady(true);
      }
    });
    return () => {
      current = false;
    };
  }, [activePortrait.fallback, activePortrait.head, hero.card, hero.head]);
  const activeSd =
    hero.forms.find((form) => form.id === activeSdForm) ?? hero.forms[0];
  const sdActions =
    hero.gameId === 'guardian-tales' && discoveredSdActions.length
      ? discoveredSdActions.map((id) => ({
          id,
          label: guardianSdActionLabel(id),
        }))
      : orderedActions(activeSd.actions);
  const [heroLevel, setHeroLevel] = useState(1);
  const [skillRanks, setSkillRanks] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      skills.map((skill) => [skill.id, skillRankAtLevel(hero, skill.id, 1)]),
    ),
  );
  const [selectorDestination, setSelectorDestination] = useState<
    'cover' | 'content'
  >('cover');
  const [selectorSection, setSelectorSection] = useState<'skills' | 'archive'>(
    'skills',
  );
  const [selectorOffset, setSelectorOffset] = useState(0);
  const [coverExited, setCoverExited] = useState(startAtContent);
  const [activeContentSection, setActiveContentSection] = useState<
    'skills' | 'archive' | null
  >(startAtContent ? initialContentSection : null);
  const [entryReady, setEntryReady] = useState(!startAtContent);
  const [heroSelectorOpen, setHeroSelectorOpen] = useState(false);
  useLayoutEffect(() => {
    if (!heroSelectorOpen) return;
    const root = document.documentElement;
    const body = document.body;
    const previous = {
      rootOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };
    const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
    // Stop any cover snap already in progress without changing the current position.
    window.scrollTo({
      top: window.scrollY,
      left: window.scrollX,
      behavior: 'instant',
    });
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (scrollbarWidth)
      body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight || '0') + scrollbarWidth}px`;
    return () => {
      root.style.overflow = previous.rootOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.paddingRight = previous.paddingRight;
    };
  }, [heroSelectorOpen]);
  const [heroSearch, setHeroSearch] = useState('');
  const [heroFilters, setHeroFilters] = useState<Record<string, string>>({});
  const filterFields = game.filters;
  const starsFacet = filterFields.find(
    (field) => field.source === 'stars',
  )?.key;
  const searchTerms = heroSearch
    .trim()
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const filteredHeroes = heroes
    .filter(
      (item) =>
        searchTerms.every((term) =>
          `${item.name} ${item.subtitle} ${item.latin} ${item.id}`
            .toLocaleLowerCase()
            .includes(term),
        ) &&
        filterFields.every(
          ({ key }) =>
            !heroFilters[key] || item.facets[key] === heroFilters[key],
        ),
    )
    .sort((a, b) =>
      starsFacet
        ? (Number(b.facets[starsFacet]) || 0) -
          (Number(a.facets[starsFacet]) || 0)
        : 0,
    );
  const resetHeroFilters = () => {
    setHeroSearch('');
    setHeroFilters({});
  };
  const passiveTrackRef = useRef<HTMLDivElement>(null);
  const passiveDragRef = useRef({
    active: false,
    startX: 0,
    startScrollLeft: 0,
  });

  const changeSkillRank = (skillId: string, change: number) => {
    setSkillRanks((current) => ({
      ...current,
      [skillId]: Math.max(
        1,
        Math.min(
          skills.find((skill) => skill.id === skillId)!.ranks.length,
          current[skillId] + change,
        ),
      ),
    }));
  };

  const commentLinkUrl = `/${hero.gameId}/t${hero.id}`;

  const openHeroSelector = (destination: 'cover' | 'content') => {
    onOpenHeroDirectory?.();
    const section = activeContentSection ?? 'skills';
    setSelectorSection(section);
    const sectionElement = document.getElementById(section);
    setSelectorOffset(
      sectionElement
        ? Math.round(-sectionElement.getBoundingClientRect().top)
        : 0,
    );
    setSelectorDestination(destination);
    setHeroSelectorOpen(true);
  };

  const scrollPassives = (direction: -1 | 1) => {
    passiveTrackRef.current?.scrollBy({
      left: direction * 340,
      behavior: 'smooth',
    });
  };

  const startPassiveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (
      hero.gameId === 'arcanatactics' &&
      event.pointerType !== 'mouse' &&
      window.matchMedia('(max-width: 760px)').matches
    ) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const track = passiveTrackRef.current;
    if (!track) return;
    passiveDragRef.current = {
      active: true,
      startX: event.clientX,
      startScrollLeft: track.scrollLeft,
    };
    track.setPointerCapture(event.pointerId);
    track.dataset.dragging = 'true';
  };

  const movePassiveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const track = passiveTrackRef.current;
    if (!track || !passiveDragRef.current.active) return;
    event.preventDefault();
    track.scrollLeft =
      passiveDragRef.current.startScrollLeft -
      (event.clientX - passiveDragRef.current.startX);
  };

  const stopPassiveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const track = passiveTrackRef.current;
    passiveDragRef.current.active = false;
    if (!track) return;
    if (track.hasPointerCapture(event.pointerId))
      track.releasePointerCapture(event.pointerId);
    delete track.dataset.dragging;
  };

  useLayoutEffect(() => {
    if (startAtContent) {
      // Keep the full cover, but position the viewport before revealing the page.
      const section = document.getElementById(initialContentSection);
      if (section && initialSectionOffset !== undefined) {
        const top = window.scrollY + section.getBoundingClientRect().top;
        const headerHeight = window.matchMedia('(min-width: 1480px)').matches
          ? 0
          : (document.querySelector('.hero-content-nav')?.getBoundingClientRect()
              .height ?? 56);
        const offset = Math.min(
          initialSectionOffset,
          Math.max(0, section.offsetHeight - headerHeight - 32),
        );
        const coverBottom =
          document.querySelector('.v2-hero')?.getBoundingClientRect().bottom ??
          0;
        window.scrollTo({
          top: Math.max(window.scrollY + coverBottom, top + offset),
          behavior: 'instant',
        });
      } else {
        section?.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    }
    setEntryReady(true);
  }, [startAtContent, initialContentSection, initialSectionOffset]);

  useEffect(() => {
    const previous = Number(
      sessionStorage.getItem(`${hero.gameId}-${hero.id}-quote-index`),
    );
    const candidates = heroQuotes
      .map((_, index) => index)
      .filter((index) => index !== previous);
    const next = candidates[Math.floor(Math.random() * candidates.length)] ?? 0;
    sessionStorage.setItem(
      `${hero.gameId}-${hero.id}-quote-index`,
      String(next),
    );
    setHeroQuote(heroQuotes[next] ?? '');
  }, []);

  useEffect(() => {
    if (!heroSelectorOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setHeroSelectorOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [heroSelectorOpen]);

  useEffect(() => {
    setSkillRanks(
      Object.fromEntries(
        skills.map((skill) => [
          skill.id,
          skillRankAtLevel(hero, skill.id, heroLevel),
        ]),
      ),
    );
  }, [heroLevel]);

  useEffect(() => {
    const updateContentState = () => {
      // Reveal the shortcuts only after the cover has fully left the viewport.
      const cover = document.querySelector('.v2-hero');
      const inContent = !cover || cover.getBoundingClientRect().bottom <= 0;
      setCoverExited(inContent);
      const readingLine =
        (window.matchMedia('(min-width: 1480px)').matches
          ? 0
          : (document.querySelector('.hero-content-nav')?.getBoundingClientRect()
              .height ?? 56)) + 24;
      const archive = document.getElementById('archive');
      setActiveContentSection(
        !inContent
          ? null
          : archive && archive.getBoundingClientRect().top <= readingLine
            ? 'archive'
            : 'skills',
      );
    };
    updateContentState();
    window.addEventListener('scroll', updateContentState, { passive: true });
    window.addEventListener('resize', updateContentState);
    return () => {
      window.removeEventListener('scroll', updateContentState);
      window.removeEventListener('resize', updateContentState);
    };
  }, []);

  useEffect(() => {
    let scrollingToSkills = false;
    let unlockTimer: ReturnType<typeof setTimeout> | undefined;
    const handleFirstScreenWheel = (event: WheelEvent) => {
      // CSS overscroll containment cannot stop a global JS scrollIntoView.
      if (document.querySelector('.v2-hero-selector-backdrop')) return;
      const hero = document.querySelector<HTMLElement>('.v2-hero');
      const skillsSection = document.getElementById('skills');
      if (!hero || !skillsSection || scrollingToSkills) return;
      const skillsTop = skillsSection.offsetTop;
      const movingToSkills =
        event.deltaY >= 8 && window.scrollY < hero.offsetHeight - 80;
      const movingToHero =
        event.deltaY <= -8 && window.scrollY > 0 && window.scrollY < skillsTop;
      if (!movingToSkills && !movingToHero) return;
      event.preventDefault();
      scrollingToSkills = true;
      (movingToSkills ? skillsSection : hero).scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'start',
      });
      unlockTimer = setTimeout(() => {
        scrollingToSkills = false;
      }, 700);
    };
    window.addEventListener('wheel', handleFirstScreenWheel, {
      passive: false,
    });
    return () => {
      window.removeEventListener('wheel', handleFirstScreenWheel);
      if (unlockTimer) clearTimeout(unlockTimer);
    };
  }, []);

  return (
    <main
      className={`v2-shell hero-theme-${coverTheme(hero)}${coverPalette ? ' hero-cover-auto' : ''}${coverPaletteReady ? ' hero-cover-ready' : ' hero-cover-pending'}${coverExited ? ' hero-content-active' : ''}`}
      data-hero-id={hero.id}
      data-game-id={hero.gameId}
      style={{
        ...(entryReady ? {} : { visibility: 'hidden' }),
        ...(coverPalette
          ? {
              '--cover-auto-base': coverPalette.base,
              '--cover-auto-end': coverPalette.end,
              '--cover-auto-glow': coverPalette.glow,
              '--cover-auto-accent': coverPalette.accent,
            }
          : {}),
      } as CSSProperties}
    >
      {coverExited && (
        <aside className="hero-content-nav" aria-label="英雄内容导航">
          <div>
            <button
              aria-label="更换头像（切换英雄）"
              aria-haspopup="dialog"
              aria-expanded={heroSelectorOpen}
              onClick={() => openHeroSelector('content')}
            >
              <img src={hero.head} alt="" />
              <span>更换头像</span>
              <RefreshCw />
            </button>
            <nav className="hero-content-shortcuts" aria-label="内容快捷导航">
              <a
                href="#skills"
                aria-current={
                  activeContentSection === 'skills' ? 'location' : undefined
                }
              >
                技能属性
              </a>
              <a
                href="#archive"
                aria-current={
                  activeContentSection === 'archive' ? 'location' : undefined
                }
              >
                档案资料
              </a>
            </nav>
          </div>
        </aside>
      )}
      <header className="v2-nav">
        <a href={`/${hero.gameId}`} className="v2-brand">
          {game.logo ? (
            <img className="hero-game-logo" src={game.logo} alt="" />
          ) : (
            <Waves />
          )}
          <span>
            {game.name}
            <br />
            <small>HERO ARCHIVE</small>
          </span>
        </a>
        <button
          className="v2-hero-switch"
          aria-label="切换英雄"
          title="切换英雄"
          aria-haspopup="dialog"
          aria-expanded={heroSelectorOpen}
          onClick={() => openHeroSelector('cover')}
        >
          {hero.head ? (
            <img src={hero.head} alt={`${hero.name}头像`} />
          ) : (
            <span aria-hidden="true" />
          )}
          <span>
            <RefreshCw />
          </span>
        </button>
      </header>

      {heroSelectorOpen && (
        <div
          className="v2-hero-selector-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setHeroSelectorOpen(false);
          }}
        >
          <section
            className="v2-hero-selector"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hero-selector-title"
          >
            <header>
              <div>
                <p>HERO DIRECTORY</p>
                <h2 id="hero-selector-title">选择英雄</h2>
              </div>
              <button
                onClick={() => setHeroSelectorOpen(false)}
                aria-label="关闭英雄选择"
              >
                <X />
              </button>
            </header>
            <div className="hero-selector-tools">
              <div className="hero-selector-controls">
                <div className="hero-selector-filters">
                  {filterFields.map(({ key, label, source }) => (
                    <div key={key} className="hero-selector-field">
                    {mobileSelector && !heroFilters[key] && (
                      <span className="hero-selector-placeholder" aria-hidden="true">{label}</span>
                    )}
                    <NativeSelect
                      key={key}
                      className={mobileSelector && !heroFilters[key] ? 'hero-selector-unset' : ''}
                      aria-label={label}
                      value={heroFilters[key] ?? ''}
                      onChange={(event) =>
                        setHeroFilters((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                    >
                      <NativeSelectOption value="">
                        {mobileSelector ? '全部' : `全部${label}`}
                      </NativeSelectOption>
                      {Array.from(
                        new Set(
                          heroes
                            .map((item) => item.facets[key])
                            .filter(Boolean),
                        ),
                      )
                        .sort()
                        .map((value) => (
                          <NativeSelectOption key={value} value={value}>
                            {value}
                            {source === 'stars' ? ' 星' : ''}
                          </NativeSelectOption>
                        ))}
                    </NativeSelect>
                    </div>
                  ))}
                </div>
                <Input
                  type="search"
                  value={heroSearch}
                  onChange={(event) => setHeroSearch(event.target.value)}
                  placeholder={mobileSelector ? '搜索英雄名称、称号或编号' : '搜索英雄名称、称号、英文名或编号'}
                  aria-label="搜索英雄"
                />
              </div>
              <div className="hero-selector-summary">
                <span role="status">
                  找到 {filteredHeroes.length} / {heroes.length} 位英雄
                </span>
                <button
                  onClick={resetHeroFilters}
                  disabled={
                    !heroSearch && !Object.values(heroFilters).some(Boolean)
                  }
                >
                  清除筛选
                </button>
              </div>
            </div>
            <div className="v2-hero-selector-grid">
              {filteredHeroes.map((item) => {
                const href = `${heroArchiveUrl(item, directory)}${selectorDestination === 'content' ? `?view=content&section=${selectorSection}&offset=${selectorOffset}` : ''}`;
                return (
                <a
                  key={`${item.gameId}:${item.id}`}
                  href={href}
                  onClick={(event) => {
                    if (
                      onNavigateHero &&
                      !event.metaKey &&
                      !event.ctrlKey &&
                      !event.shiftKey &&
                      !event.altKey &&
                      event.button === 0
                    ) {
                      event.preventDefault();
                      setHeroSelectorOpen(false);
                      if (item.id !== hero.id) onNavigateHero(href);
                      return;
                    }
                    setHeroSelectorOpen(false);
                  }}
                  className={item.id === hero.id ? 'active' : ''}
                >
                  <img
                    src={item.head || undefined}
                    alt={`${item.name}卡面`}
                    loading="lazy"
                    width={160}
                    height={220}
                  />
                  <b title={item.name}>{item.name}</b>
                  {item.id === hero.id && <em>当前</em>}
                </a>
                );
              })}
            </div>
            {filteredHeroes.length === 0 && (
              <div className="hero-selector-empty">
                <p>没有找到符合条件的英雄</p>
                <button onClick={resetHeroFilters}>清除筛选，查看全部</button>
              </div>
            )}
          </section>
        </div>
      )}

      <section className="v2-hero">
        <div className="v2-grid" aria-hidden="true" />
        <article className="v2-copy">
          <p className="v2-kicker">{hero.kicker}</p>
          {hero.gameId === 'arcanatactics' && hero.forms.length > 1 && (
            <div className="v2-form-switch v2-form-switch-mobile" aria-label="切换皮肤">
              {hero.forms.map((form) => (
                <button
                  key={form.id}
                  className={activeForm === form.id ? 'active' : ''}
                  aria-pressed={activeForm === form.id}
                  onClick={() => setActiveForm(form.id)}
                >
                  <img src={form.head || hero.head || form.fallback || undefined} alt={form.name} />
                </button>
              ))}
            </div>
          )}
          <h1>{hero.name}</h1>
          <p className="v2-latin">{hero.latin}</p>
          {coverIntro && <p className="v2-motto">“{coverIntro}”</p>}
          <div className="v2-identity">
            {hero.stars > 0 && (
              <span className="v2-rarity" aria-label={`${hero.stars}星英雄`}>
                <b aria-hidden="true">{'★'.repeat(hero.stars)}</b>
              </span>
            )}
            {hero.traits.map((trait) => (
              <span
                key={trait.label}
                aria-label={`${trait.label}：${trait.name}`}
              >
                <b>
                  <img src={trait.icon} alt="" />
                  {trait.name}
                </b>
              </span>
            ))}
          </div>
          <div className="v2-actions">
            <a href="#skills">
              <span>
                <b>资料详情</b>
              </span>
              <ArrowDown />
            </a>
            <a href="#archive">
              <span>
                <b>评测攻略</b>
                <small>敬请期待</small>
              </span>
              <ArrowRight />
            </a>
          </div>
        </article>
        <article className="v2-art">
          <div className="v2-aura" />
          <SpinePlayer
            asset={activePortrait.portrait}
            fallback={activePortrait.fallback ?? activePortrait.head}
            title={[hero.name, activePortrait.name].filter(Boolean).join(' · ')}
            className="spine-portrait"
            eager
          />
          {hero.forms.length > 1 && (
            <div className="v2-form-switch">
              {hero.forms.map((form) => (
                <button
                  key={form.id}
                  className={activeForm === form.id ? 'active' : ''}
                  aria-pressed={activeForm === form.id}
                  onClick={() => setActiveForm(form.id)}
                >
                  <img
                    src={form.head || hero.head || form.fallback || undefined}
                    alt={form.name}
                  />
                </button>
              ))}
            </div>
          )}
        </article>
      </section>

      {hero.gameId === 'lostsword' ? (
        <LostSwordHeroContent hero={hero} />
      ) : (
        <>
      <section className="v2-paper" id="skills">
        <div className="v2-content">
          <header className="v2-section-head">
            <div>
              <p>COMBAT KIT</p>
              <h2>技能属性</h2>
            </div>
            <span>{hero.combatNote}</span>
          </header>
          <div className="v2-skill-layout">
            {hero.stats.length > 0 && (
              <aside className="v2-stats">
                <p>LEVEL CONTROL · LV.{String(heroLevel).padStart(2, '0')}</p>
                <h3>等级调整</h3>
                <div className="v2-level-control">
                  <div>
                    <button
                      onClick={() =>
                        setHeroLevel((level) => Math.max(1, level - 1))
                      }
                      disabled={heroLevel === 1}
                      aria-label="降低英雄等级"
                    >
                      <Minus />
                    </button>
                    <output>
                      <span>LV.</span>
                      <b>{String(heroLevel).padStart(2, '0')}</b>
                    </output>
                    <button
                      onClick={() =>
                        setHeroLevel((level) =>
                          Math.min(hero.maxLevel, level + 1),
                        )
                      }
                      disabled={heroLevel === hero.maxLevel}
                      aria-label="提高英雄等级"
                    >
                      <Plus />
                    </button>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max={hero.maxLevel}
                    value={heroLevel}
                    onChange={(event) =>
                      setHeroLevel(Number(event.target.value))
                    }
                    aria-label="英雄等级"
                  />
                  <small>
                    <span>01</span>
                    <span>{hero.maxLevel}</span>
                  </small>
                </div>
                <dl className="v2-stat-values">
                  {statsAtLevel(hero, heroLevel).map((stat) => (
                    <div key={stat.key}>
                      <dt>{stat.label}</dt>
                      <dd>{stat.display}</dd>
                    </div>
                  ))}
                </dl>
              </aside>
            )}
            <div className="v2-skills">
              {skills.map((skill) => {
                const rank = skillRanks[skill.id];
                const guardianZoomedIcon =
                  hero.gameId === 'guardian-tales' &&
                  ['attack', 'weapon', 'weapon-enhance'].includes(skill.id);
                const guardianPartyIcon =
                  hero.gameId === 'guardian-tales' && skill.id === 'party-passive';
                return (
                  <article
                    key={skill.id}
                    className="v2-skill-card"
                    data-skill-id={skill.id}
                  >
                    {guardianPartyIcon ? (
                      <img
                        className="v2-skill-icon v2-skill-party-icon"
                        src={staticUrl('/assets/guardian-tales/icons/party-flag.svg')}
                        alt=""
                      />
                    ) : guardianZoomedIcon ? (
                      <span
                        className={`v2-skill-icon v2-skill-icon--${skill.id}`}
                        style={{ backgroundImage: `url(${skill.icon})` }}
                        aria-hidden="true"
                      />
                    ) : (
                      <img src={skill.icon} alt="" />
                    )}
                    <div className="v2-skill-main">
                      <p>{skill.type}</p>
                      <div className="v2-skill-title-row">
                        <h3>{skill.name}</h3>
                        {skill.ranks.length > 1 && (
                          <div
                            className="v2-rank-control"
                            aria-label={`${skill.name}档位调节`}
                          >
                            <button
                              onClick={() => changeSkillRank(skill.id, -1)}
                              disabled={rank === 1}
                              aria-label={`降低${skill.name}档位`}
                            >
                              <Minus />
                            </button>
                            <output>
                              档位 <b>{rank}</b>
                            </output>
                            <button
                              onClick={() => changeSkillRank(skill.id, 1)}
                              disabled={rank === skill.ranks.length}
                              aria-label={`提高${skill.name}档位`}
                            >
                              <Plus />
                            </button>
                          </div>
                        )}
                      </div>
                      <span>{skill.ranks[rank - 1]}</span>
                    </div>
                  </article>
                );
              })}
            </div>
            {hero.gameId === 'guardian-tales' && guardianPassiveGroups.length > 0 ? (
              <section className="v2-passives v2-guardian-passives" aria-labelledby="passive-title">
                <header>
                  <div>
                    <p>PASSIVE PROGRESSION</p>
                    <h3 id="passive-title">等级成长</h3>
                  </div>
                </header>
                <div className="v2-guardian-passive-groups">
                  {guardianPassiveGroups.map((group) => (
                    <section className="v2-passive-group" key={group.id} aria-labelledby={`${group.id}-title`}>
                      <header>
                        <h4 id={`${group.id}-title`}>{group.title}</h4>
                        <div className="v2-passive-nav">
                          <button
                            onClick={(event) =>
                              event.currentTarget
                                .closest('.v2-passive-group')
                                ?.querySelector<HTMLElement>('.v2-passive-track')
                                ?.scrollBy({ left: -340, behavior: 'smooth' })
                            }
                            aria-label={`查看${group.title}前面的等级`}
                          >
                            <ArrowLeft />
                          </button>
                          <button
                            onClick={(event) =>
                              event.currentTarget
                                .closest('.v2-passive-group')
                                ?.querySelector<HTMLElement>('.v2-passive-track')
                                ?.scrollBy({ left: 340, behavior: 'smooth' })
                            }
                            aria-label={`查看${group.title}后面的等级`}
                          >
                            <ArrowRight />
                          </button>
                        </div>
                      </header>
                      <div
                        className="v2-passive-track"
                        tabIndex={0}
                        aria-label={`${group.title}等级成长效果`}
                      >
                        {group.items.map((item, index) => (
                          <article
                            className="v2-passive-card"
                            key={`${item.level}-${index}`}
                          >
                            <div>
                              <span>LV.</span>
                              <b>{String(item.level).padStart(2, '0')}</b>
                            </div>
                            <p>本级解锁</p>
                            <ul>
                              {item.effects.map((effect, effectIndex) => (
                                <li key={`${effect}-${effectIndex}`}>{effect}</li>
                              ))}
                            </ul>
                          </article>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </section>
            ) : passiveLevels.length > 0 && (
              <section className="v2-passives" aria-labelledby="passive-title">
                <header>
                  <div>
                    <p>PASSIVE PROGRESSION</p>
                    <h3 id="passive-title">等级成长</h3>
                  </div>
                  <div className="v2-passive-nav">
                    <button
                      onClick={() => scrollPassives(-1)}
                      aria-label="查看前面的等级"
                    >
                      <ArrowLeft />
                    </button>
                    <button
                      onClick={() => scrollPassives(1)}
                      aria-label="查看后面的等级"
                    >
                      <ArrowRight />
                    </button>
                  </div>
                </header>
                <div
                  className="v2-passive-track"
                  ref={passiveTrackRef}
                  tabIndex={0}
                  aria-label="英雄等级成长效果，可按住卡片左右拖动"
                  onPointerDown={startPassiveDrag}
                  onPointerMove={movePassiveDrag}
                  onPointerUp={stopPassiveDrag}
                  onPointerCancel={stopPassiveDrag}
                >
                  {passiveLevels.map((item, index) => (
                    <article
                      className="v2-passive-card"
                      key={`${item.level}-${index}`}
                    >
                      <div>
                        <span>LV.</span>
                        <b>{String(item.level).padStart(2, '0')}</b>
                      </div>
                      <p>本级解锁</p>
                      <ul>
                        {item.effects.map((effect, effectIndex) => (
                          <li key={`${effect}-${effectIndex}`}>{effect}</li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </section>

      <section className="v2-archive" id="archive">
        <div className="v2-archive-inner">
          <header className="v2-archive-head">
            <div>
              <p>CHARACTER ARCHIVE · NO.{hero.id}</p>
              <h2>档案资料</h2>
            </div>
            <span>
              {hero.subtitle} / {hero.name}
            </span>
          </header>
          <div className="v2-archive-grid">
            {(hero.story || hero.forms.some((form) => form.sd)) && (
              <article className="v2-story-panel">
                {hero.forms.some((form) => form.sd) && (
                  <>
                    <header>
                      <div>
                        <p>BATTLE UNIT</p>
                        <h3>SD 小人动画</h3>
                      </div>
                    </header>
                    <SpinePlayer
                      asset={activeSd.sd}
                      fallback={activeSd.head}
                      title={`${hero.name} · SD 小人动画`}
                      className="v2-sd-stage"
                      animation={activeSdAction}
                      onAnimations={
                        hero.gameId === 'guardian-tales'
                          ? (animations) => {
                              const orderedAnimations = orderGuardianSdActions(animations);
                              setDiscoveredSdActions(orderedAnimations);
                              setActiveSdAction((current) => {
                                if (
                                  current !== 'wait' &&
                                  orderedAnimations.includes(current)
                                ) {
                                  return current;
                                }
                                return orderedAnimations[0] ?? current;
                              });
                            }
                          : undefined
                      }
                    />
                    <div className="v2-sd-controls">
                      <div className="v2-sd-forms" aria-label="切换小人形态">
                        {hero.forms
                          .filter((form) => form.sd)
                          .map((form) => (
                            <button
                              key={form.id}
                              className={
                                activeSdForm === form.id ? 'active' : ''
                              }
                              aria-pressed={activeSdForm === form.id}
                              onClick={() => {
                                setActiveSdForm(form.id);
                                setDiscoveredSdActions([]);
                                setActiveSdAction('wait');
                              }}
                            >
                              <img
                                src={
                                  form.head ||
                                  hero.head ||
                                  form.fallback ||
                                  undefined
                                }
                                alt={`${form.name}小人`}
                              />
                            </button>
                          ))}
                      </div>
                      <div className="v2-sd-actions" aria-label="切换小人动作">
                        {sdActions.map((action) => (
                          <button
                            key={action.id}
                            className={
                              activeSdAction === action.id ? 'active' : ''
                            }
                            aria-pressed={activeSdAction === action.id}
                            onClick={() => setActiveSdAction(action.id)}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {hero.story && (
                  <section className="v2-story-copy">
                    <p>HERO STORY</p>
                    <h3>
                      {hero.gameId === 'guardian-tales' ? '背景故事' : '英雄介绍'}
                    </h3>
                    {hero.gameId === 'guardian-tales' ? (
                      <div className="v2-story v2-guardian-story">
                        {hero.story
                          .split(/\n\s*\n/)
                          .filter(Boolean)
                          .map((paragraph, index) => (
                            <p key={index}>{paragraph.trim()}</p>
                          ))}
                      </div>
                    ) : (
                      <p className="v2-story">{hero.story}</p>
                    )}
                  </section>
                )}
              </article>
            )}
            {hero.formula && (
              <article className="v2-summon-panel">
                <header>
                  <p>SUMMON FORMULA</p>
                  <h3>合成公式</h3>
                  <span>{hero.formula.cost}</span>
                </header>
                <div className="v2-formula">
                  {hero.formula.materials.map((material, index) => (
                    <Fragment key={`${material.name}-${index}`}>
                      {index > 0 && <span>＋</span>}
                      <div>
                        <img src={material.icon} alt={material.name} />
                        <b>{material.name}</b>
                      </div>
                    </Fragment>
                  ))}
                  <span>＝</span>
                  <div className="result">
                    {hero.icon ? (
                      <img src={hero.icon} alt={hero.name} />
                    ) : null}
                    <b>{hero.name}</b>
                  </div>
                </div>
              </article>
            )}
            {heroQuotes.length > 0 && (
              <article className="v2-quotes-panel">
                <header>
                  <p>VOICE ARCHIVE</p>
                  <h3>英雄台词</h3>
                </header>
                <ol>
                  {heroQuotes.map((quote, index) => (
                    <li key={`${index}-${quote.slice(0, 24)}`}>
                      <q>{quote}</q>
                    </li>
                  ))}
                </ol>
              </article>
            )}
          </div>
        </div>
      </section>

        </>
      )}

      <PlatformEngagement
        linkUrl={commentLinkUrl}
        subject="英雄"
        title={hero.name}
        maxWidth={1080}
        visible={coverExited}
      />
    </main>
  );
}
